import { useState } from "react";
import { toast } from "sonner";
import { Mail, Globe, MapPin, CalendarHeart, Crown, Star, Loader2 } from "lucide-react";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { API, formatApiErrorDetail } from "@/lib/api";
import { SITE } from "@/content";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  event_type: SITE.eventTypes[0],
  event_date: "",
  guests: "",
  message: "",
};

const PERKS = [
  { icon: Crown, text: "First pick of auspicious dates before the public opening" },
  { icon: Star, text: "A personal walkthrough of the venue with our events team" },
  { icon: CalendarHeart, text: "Inaugural-season courtesies for early-booked families" },
];

export const Booking = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const payload = { ...form, guests: form.guests ? Number(form.guests) : null };
      const { data } = await axios_post(payload);
      toast.success(
        data?.email_sent
          ? "Enquiry sent — it has reached our inbox. We'll reply to your email shortly."
          : "Enquiry received — we'll get back to you shortly.",
        { duration: 6000 }
      );
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err), { duration: 6000 });
    } finally {
      setSending(false);
    }
  };

  const axios_post = (payload) =>
    fetch(`${API}/bookings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(), { response: { data, status: res.status } });
      return { data };
    });

  return (
    <section id="book" data-testid="booking-inquiry-section" className="relative py-24 lg:py-32 bg-[#180407] overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 500px at 15% 20%, rgba(212,175,55,0.10), transparent 60%), radial-gradient(700px 500px at 90% 90%, rgba(74,14,23,0.8), transparent 65%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div>
          <SectionHeading
            eyebrow="Booking & Enquiries"
            title={
              <>
                Reserve Your Date
                <br />
                <span className="italic gold-text">Before the Doors Open</span>
              </>
            }
            sub="The mandap opens shortly, and our calendar is already being drawn. Send us your enquiry — every request lands directly in our inbox and is answered personally by email."
          />

          <FadeUp delay={0.15} className="mt-10 space-y-5">
            {PERKS.map((p) => (
              <div key={p.text} className="flex items-start gap-4">
                <span className="shrink-0 rounded-full border border-[#D4AF37]/40 p-2.5 text-[#D4AF37]">
                  <p.icon size={15} />
                </span>
                <p className="text-sm font-light text-[#FAF6F0]/85 leading-relaxed pt-2">{p.text}</p>
              </div>
            ))}
          </FadeUp>

          <FadeUp delay={0.2} className="mt-12 glass-card rounded-2xl p-7 space-y-4">
            <a
              data-testid="booking-contact-email"
              href={`mailto:${SITE.email}`}
              className="flex items-center gap-3.5 text-[#F3E5AB] hover:text-[#D4AF37] transition-colors duration-300 text-sm sm:text-base"
            >
              <Mail size={17} className="text-[#D4AF37]" /> {SITE.email}
            </a>
            <p className="flex items-center gap-3.5 text-sm text-[#A39791]">
              <Globe size={17} className="text-[#D4AF37]" /> {SITE.domain}
            </p>
            <p className="flex items-start gap-3.5 text-sm text-[#A39791]">
              <MapPin size={17} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <span>{SITE.addressLine}</span>
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.1}>
          <form
            data-testid="booking-form"
            onSubmit={submit}
            className="glass-card rounded-3xl p-7 sm:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="bf-name" className="block text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">
                  Full Name *
                </label>
                <input
                  id="bf-name"
                  data-testid="booking-field-name"
                  className="field-lux"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="bf-phone" className="block text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">
                  Phone *
                </label>
                <input
                  id="bf-phone"
                  data-testid="booking-field-phone"
                  className="field-lux"
                  required
                  minLength={5}
                  maxLength={25}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+91"
                />
              </div>
            </div>

            <div>
              <label htmlFor="bf-email" className="block text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">
                Email *
              </label>
              <input
                id="bf-email"
                data-testid="booking-field-email"
                type="email"
                className="field-lux"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="bf-type" className="block text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">
                  Occasion
                </label>
                <select
                  id="bf-type"
                  data-testid="booking-field-event-type"
                  className="field-lux"
                  value={form.event_type}
                  onChange={set("event_type")}
                >
                  {SITE.eventTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="bf-date" className="block text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">
                  Preferred Date
                </label>
                <input
                  id="bf-date"
                  data-testid="booking-field-date"
                  type="date"
                  min={today}
                  className="field-lux"
                  value={form.event_date}
                  onChange={set("event_date")}
                />
              </div>
            </div>

            <div>
              <label htmlFor="bf-guests" className="block text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">
                Estimated Guests
              </label>
              <input
                id="bf-guests"
                data-testid="booking-field-guests"
                type="number"
                min={1}
                max={100000}
                className="field-lux"
                value={form.guests}
                onChange={set("guests")}
                placeholder="e.g. 500"
              />
            </div>

            <div>
              <label htmlFor="bf-message" className="block text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">
                Message
              </label>
              <textarea
                id="bf-message"
                data-testid="booking-field-message"
                rows={4}
                maxLength={2000}
                className="field-lux resize-none"
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us about your celebration..."
              />
            </div>

            <button
              type="submit"
              data-testid="booking-submit-button"
              disabled={sending}
              className="relative overflow-hidden sheen w-full rounded-full bg-[#D4AF37] py-4 text-[13px] font-semibold uppercase tracking-[0.22em] text-[#2C070B] shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:shadow-[0_0_46px_rgba(212,175,55,0.5)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-400 disabled:opacity-70 disabled:translate-y-0 flex items-center justify-center gap-2.5"
            >
              {sending && <Loader2 size={16} className="animate-spin" />}
              {sending ? "Sending Enquiry" : "Send Booking Enquiry"}
            </button>
            <p className="text-center text-[11px] text-[#A39791]">
              Your enquiry arrives as an email at {SITE.email} — we reply personally.
            </p>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};

export default Booking;
